import React, { useState, useEffect } from 'react';
import { useStore } from 'effector-react';
import { Checkbox, Position } from '@blueprintjs/core';
import Circle from '@app/components/Circle';
import Flex from '@app/components/Flex';
import { PlatformClientUserFilterData } from '@app/containers/pages/Clients/types';
import PlatformListFilter from '@app/components/PlatformListFilter';
import { fetchStaticData } from '@app/containers/pages/Clients/store/effects';
import { $isQueryHydrationFinished } from '@app/containers/pages/Clients/store/states';
import PlatformFilterButton from '@app/components/PlatformFilterButton';
import { platformClientUserFiltersInformation } from '@app/containers/pages/Clients/consts';
import { PlatformClientUserFilterType } from '@app/containers/pages/Clients/enums';
import { setPlatformClientUserFilterData } from '@app/containers/pages/Clients/store/events';

export interface ClientUserTeamsPlatformFilterProps {
    defaultIsOpen?: boolean;
    platformClientUserFilterData: PlatformClientUserFilterData;
    onRemove: () => void;
}

export type Props = ClientUserTeamsPlatformFilterProps;

// Teams are compared by ID, so swapping one team for another one is applied as well
const getTeamIdsKey = (teams: PlatformClientUserFilterData['teams']) =>
    teams
        .map((team) => team.id)
        .sort()
        .join(',');

const ClientUserTeamsPlatformFilter: React.FC<Props> = ({
    defaultIsOpen = false,
    platformClientUserFilterData,
    onRemove,
}) => {
    const isQueryHydrationFinished = useStore($isQueryHydrationFinished);

    const [isOpen, setIsOpen] = useState(defaultIsOpen);
    const [isTeamsInitialized, setIsTeamsInitialized] = useState(false);
    const [selectedTeams, setSelectedTeams] = useState<PlatformClientUserFilterData['teams']>([]);
    const [foundTeams, setFoundTeams] = useState<PlatformClientUserFilterData['teams']>([]);

    useEffect(() => {
        if (isQueryHydrationFinished) {
            fetchStaticData({ team: '1' }).then((staticData) => {
                setFoundTeams(staticData.team);
                setSelectedTeams(
                    staticData.team.filter((st) => platformClientUserFilterData.teams.find((t) => t.id === st.id))
                );
                setTimeout(() => setIsTeamsInitialized(true), 0);
            });
        }

        // eslint-disable-next-line
    }, [isQueryHydrationFinished]);

    useEffect(() => {
        if (platformClientUserFilterData.teams.length === 0) {
            setSelectedTeams([]);
        }
    }, [platformClientUserFilterData.teams]);

    const getFilterTagLabel = () => {
        if (selectedTeams.length === 0) return '';

        return selectedTeams
            .map((t, index) => {
                if (index === selectedTeams.length - 1) {
                    // Last element
                    return `${t.title}`;
                }

                return `${t.title}, `;
            })
            .join('');
    };

    const getFilterTagTitle = () => {
        if (selectedTeams.length > 0) {
            // At least 1 team selected
            return 'Team:';
        }

        return 'Team';
    };

    const handleRemoveFilter = () => {
        if (selectedTeams.length !== 0) {
            // At least 1 team was selected

            setSelectedTeams([]);

            if (platformClientUserFilterData.teams.length !== 0) {
                // Reset team to empty array
                setPlatformClientUserFilterData({ ...platformClientUserFilterData, teams: [] });
            }

            setIsOpen(false);
        }

        if (!platformClientUserFiltersInformation[PlatformClientUserFilterType.CLIENT_TEAMS].isPinned) {
            // Platform team filter is NOT pinned, so it can be removed
            onRemove();
        }
    };

    const handleCheckboxClick = (team: PlatformClientUserFilterData['teams'][number]) => {
        if (!!selectedTeams.find((selectedTeam) => selectedTeam.id === team.id)) {
            // Team is already checked
            setSelectedTeams(selectedTeams.filter((selectedTeam) => selectedTeam.id !== team.id));
        } else {
            // Team is NOT checked yet
            setSelectedTeams([...selectedTeams, team]);
        }
    };

    const handleApplyFilter = () => {
        if (getTeamIdsKey(selectedTeams) !== getTeamIdsKey(platformClientUserFilterData.teams)) {
            // Selected teams changed since opening the filter
            setPlatformClientUserFilterData({ ...platformClientUserFilterData, teams: selectedTeams });
        }
    };

    const handleCloseFilter = () => {
        setIsOpen(false);

        if (platformClientUserFiltersInformation[PlatformClientUserFilterType.CLIENT_TEAMS].isPinned) {
            return;
        }

        if (selectedTeams.length === 0) {
            onRemove();
        }
    };

    const renderRow = (team: PlatformClientUserFilterData['teams'][number]) => {
        return (
            <li className="mb-1" key={team.id}>
                <Flex justify="space-between" align="center">
                    <Checkbox
                        checked={!!selectedTeams.find((selectedTeam) => selectedTeam.id === team.id)}
                        className="mr-2"
                        label={team.title}
                        onClick={() => handleCheckboxClick(team)}
                    />

                    <Circle size="11px" color={team.color} />
                </Flex>
            </li>
        );
    };

    return (
        <PlatformListFilter<PlatformClientUserFilterData['teams'][number]>
            isOpen={isOpen}
            loading={!isTeamsInitialized}
            removable={selectedTeams.length > 0}
            title="Team"
            position={Position.BOTTOM_LEFT}
            minWidth={320}
            maxListHeight={400}
            list={foundTeams}
            itemRenderer={renderRow}
            onApply={handleApplyFilter}
            onClose={handleCloseFilter}
            onRemove={handleRemoveFilter}
        >
            <PlatformFilterButton
                icon={platformClientUserFiltersInformation[PlatformClientUserFilterType.CLIENT_TEAMS].icon}
                onClick={() => setIsOpen(!isOpen)}
                label={getFilterTagLabel()}
                title={getFilterTagTitle()}
            />
        </PlatformListFilter>
    );
};

export default ClientUserTeamsPlatformFilter;
